import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { createScheduledEmail } from '@/store/slices/scheduled-emails';
import {
  selectProjectThemeMode,
  selectProjectThemePack,
  selectWorkingProject,
  selectWorkingProjectUuid,
} from '@/store/slices/project/selectors';
import { selectUserChannel } from '@/store/slices/user/selectors';
import { campaignToRegistrationEmail, campaignToScheduledEmailRequest } from '@/utils/scheduled-email-campaign';
import { EmailTypeEnum } from '@/types/email.types';
import { useLoadCampaignTemplates } from './hooks/use-load-campaign-templates';
import TemplateSelectionModal from './template-selection-modal';
import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { description, eventImage, getHeaderImage } from '../templates/utils/utils';
import useLanguageParams from '@/utils/use-language-params';
import type { LanguagesAbbr } from '@/types/translation.types';
import SelectSingle from '@ui/select/react-select/select-single';
import { selectScheduledEmails } from '@/store/slices/scheduled-emails/selectors';
import { campaignTemplatesDefaults, createContrastOverrides } from '../templates';
import { addRecentTemplate } from '@/store/slices/campaigns';
import { campaignSelectTypes } from '../utils';

interface CreateCampaignFlowProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateCampaignFlow({ isOpen, onClose }: CreateCampaignFlowProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useLoadCampaignTemplates();
  const projectUuid = useAppSelector(selectWorkingProjectUuid);
  const workingProject = useAppSelector(selectWorkingProject);
  const currentTheme = useAppSelector(selectProjectThemePack);
  const currentMode = useAppSelector(selectProjectThemeMode);
  const channel = useAppSelector(selectUserChannel);
  const language = useLanguageParams();
  const [type, setType] = useState<EmailTypeEnum>(EmailTypeEnum.event_invite);
  const [step, setStep] = useState<'template' | 'title'>('template');
  const [pendingTemplate, setPendingTemplate] = useState<{ uuid: string; category: string } | null>(null);
  const [title, setTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const scheduledEmails = useAppSelector(selectScheduledEmails);

  const languages = useMemo(() => {
    if (!workingProject) return [];
    return Array.from(new Set(workingProject.sessions.flatMap((s) => s.languages)));
  }, [workingProject]);

  const registrationDisabled = useMemo(
    () => scheduledEmails?.some((email) => email.email_type === EmailTypeEnum.event_registration_email),
    [scheduledEmails]
  );

  const handleClose = () => {
    setStep('template');
    setPendingTemplate(null);
    setTitle('');
    setIsCreating(false);
    onClose();
  };

  const handleTemplateSelect = (templateUuid: string, category: string) => {
    setPendingTemplate({ uuid: templateUuid, category });
    setStep('title');
  };

  const handleTitleConfirm = async () => {
    if (
      !title.trim() ||
      !pendingTemplate ||
      !projectUuid ||
      !channel ||
      !workingProject ||
      !currentTheme ||
      !currentMode
    )
      return;
    setIsCreating(true);
    const defaults = campaignTemplatesDefaults.get(pendingTemplate.uuid);
    if (!defaults) return;
    const colors = currentTheme.colors[currentMode];

    let request;
    const overrideColors = createContrastOverrides(colors, defaults, currentMode);

    if (type !== EmailTypeEnum.event_registration_email) {
      request = await campaignToScheduledEmailRequest(
        {
          uuid: '',
          title: title.trim(),
          template_id: pendingTemplate.uuid,
          state: 'draft',
          stats: { sent: 0, openRate: 0, clickRate: 0 },
        },
        {
          event_uuid: projectUuid,
          channel: channel.channel,
          email_type: type,
          languages: languages || ['en'],
          baseLanguage: workingProject.settings.i18n?.base || 'en',
        },
        {
          title: workingProject.name,
          description: description(workingProject, (language ?? 'en') as LanguagesAbbr, 317, 2, 14),
          salutation: defaults.salutation ?? "You've been invited to",
          startIntro: defaults.startIntro ?? 'The show starts on',
          cta: defaults.cta ?? 'REGISTER',
          mainImage: eventImage(workingProject),
          headingImage: getHeaderImage(workingProject, channel.logos.color),
          headingFont: defaults.headingFont ?? 'Arial',
          bodyFont: defaults.bodyFont ?? 'Arial',
          ...overrideColors,
        }
      );
    } else {
      request = await campaignToRegistrationEmail(
        {
          uuid: '',
          title: title.trim(),
          template_id: pendingTemplate.uuid,
          state: 'draft',
          stats: { sent: 0, openRate: 0, clickRate: 0 },
        },
        {
          event_uuid: projectUuid,
          channel: channel.channel,
          email_type: type,
          languages: workingProject.settings.i18n?.event_languages || ['en'],
          baseLanguage: workingProject.settings.i18n?.base || 'en',
        },
        {
          title: workingProject.name,
          description: description(workingProject, (language ?? 'en') as LanguagesAbbr, 317, 2, 14),
          mainImage: eventImage(workingProject),
          headingImage: getHeaderImage(workingProject, channel.logos.color),
          headingFont: defaults.headingFont ?? 'Arial',
          bodyFont: defaults.bodyFont ?? 'Arial',
          ...overrideColors,
        }
      );
    }

    const result = await dispatch(createScheduledEmail(request));
    if (createScheduledEmail.fulfilled.match(result)) {
      handleClose();
      dispatch(addRecentTemplate(pendingTemplate.uuid));
      navigate(`${result.payload.uuid}/edit`);
    } else {
      setIsCreating(false);
    }
  };

  return (
    <>
      <TemplateSelectionModal
        isOpen={isOpen && step === 'template'}
        onClose={handleClose}
        onSelectTemplate={handleTemplateSelect}
        loading={loading}
      />

      <Modal
        size="medium"
        open={isOpen && step === 'title'}
        onRequestClose={handleClose}
        RenderHeaderContent={() => (
          <Container height="70px" className="padding-t-24 padding-h-40">
            <Text tag="h4" className="H4">
              Name your campaign
            </Text>
          </Container>
        )}
        RenderFooterContent={() => (
          <Container className="flex fd-row jc-flex-end ai-center padding-h-40 padding-v-24 gap-8">
            <Button onClick={handleClose} design="LocalBttnSec" height="53px">
              Cancel
            </Button>
            <Button
              onClick={handleTitleConfirm}
              height="53px"
              hoverOpacity={0.8}
              disabled={!title.trim() || isCreating}
              loading={isCreating}
            >
              Create
            </Button>
          </Container>
        )}
      >
        <Container className="padding-h-40 padding-v-16">
          <Container className="margin-b-8">
            <Text className="L2 position-relative left-14 top--2">Campaign Type</Text>
            <SelectSingle
              value={type}
              options={campaignSelectTypes(registrationDisabled)}
              onChange={(value) => setType(value as EmailTypeEnum)}
              placeholder="Select Type"
              width="100%"
            />
          </Container>
          <label className="margin-l-16 P2">Title</label>
          <InputWithOverlay
            inputHeight="40px"
            inputProps={{
              className: 'S2',
              placeholder: 'Enter campaign title',
              value: title,
              onChange: (e) => setTitle(e.target.value),
              onKeyDown: (e) => e.key === 'Enter' && handleTitleConfirm(),
              autoFocus: true,
            }}
          />
        </Container>
      </Modal>
    </>
  );
}
